// shared/survey/engine/questions.ts
// Generic question routing engine — mirrors diagnostic/src/engine/questions.ts

import type { SurveyQuestion, QuestionCondition, Answers } from '../types';

function evaluateCondition(condition: QuestionCondition, answers: Answers): boolean {
  const answer = answers[condition.question];
  if (answer === undefined) return false;

  switch (condition.operator) {
    case 'in':
      return condition.values.includes(answer as string | number | boolean);
    case 'not_in':
      return !condition.values.includes(answer as string | number | boolean);
    case 'equals':
      return answer === condition.values[0];
    case 'greater_than':
      return typeof answer === 'number' && answer > (condition.values[0] as number);
    case 'less_than':
      return typeof answer === 'number' && answer < (condition.values[0] as number);
    default:
      return false;
  }
}

function isQuestionEligible(question: SurveyQuestion, answers: Answers): boolean {
  if (!question.conditions || question.conditions.length === 0) return true;
  return question.conditions.every(c => evaluateCondition(c, answers));
}

export function getEligibleQuestions(questions: SurveyQuestion[], answers: Answers): SurveyQuestion[] {
  return questions
    .filter(q => isQuestionEligible(q, answers))
    .sort((a, b) => a.order - b.order);
}

export function getNextQuestion(questions: SurveyQuestion[], answers: Answers): SurveyQuestion | null {
  const eligible = getEligibleQuestions(questions, answers);
  const unanswered = eligible.filter(q => !(q.id in answers));
  return unanswered[0] || null;
}

export function getProgress(questions: SurveyQuestion[], answers: Answers): { current: number; estimated: number } {
  const answered = Object.keys(answers).length;
  const eligible = getEligibleQuestions(questions, answers);
  const remaining = eligible.filter(q => !(q.id in answers)).length;
  return {
    current: answered,
    estimated: answered + remaining,
  };
}

export function getInvalidatedAnswers(
  questions: SurveyQuestion[],
  answers: Answers,
  changedQuestionId: string
): string[] {
  const invalidated: string[] = [];
  for (const qId of Object.keys(answers)) {
    if (qId === changedQuestionId) continue;
    const question = questions.find(q => q.id === qId);
    if (!question) continue;
    if (!isQuestionEligible(question, answers)) {
      invalidated.push(qId);
    }
  }
  return invalidated;
}
